import { ReactNode, createContext, useContext, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { TriggerAddEditModalContextProps } from './TriggerAddEditModal.types';
import { TriggerAddEditModalFormValues } from './TriggerAddEditModal.schema';

const TriggerAddEditModalContext = createContext<
  TriggerAddEditModalContextProps | undefined
>(undefined);

export const TriggerAddEditModalContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { control } = useFormContext<TriggerAddEditModalFormValues>();

  /**
   * From https://react-hook-form.com/docs/usefieldarray we can only use `useFieldArray` for a given field once per
   * name, thus since we need to manipulate it from multiple places, we need it here in a context so it can be
   * shared/manipulated between multiple components.
   */
  const conditionsFieldArray = useFieldArray({ control, name: 'conditions' });

  const contextValue = useMemo(
    () => ({
      conditionsFieldArray,
    }),
    [conditionsFieldArray],
  );

  return (
    <TriggerAddEditModalContext.Provider value={contextValue}>
      {children}
    </TriggerAddEditModalContext.Provider>
  );
};

export const useTriggerAddEditModalContext = () => {
  const context = useContext(TriggerAddEditModalContext);

  if (!context) {
    throw new Error(
      'useTriggerAddEditModalContext must be used within a TriggerAddEditModalContextProvider',
    );
  }

  return context;
};
